import { BLOCKS, blockDef, type BlockDef, type ToolKind } from './blocks.ts';

/**
 * Item registry.
 *
 * Every placeable block gets an item automatically; on top of that there are
 * tools, armour, food and crafting materials. Item sprites, like block
 * textures, are procedural — described here and rasterised by the asset
 * generator into a second texture array.
 */

export type ItemSpriteShape =
  | 'pickaxe' | 'axe' | 'shovel' | 'sword' | 'hoe' | 'shears' | 'flint_and_steel'
  | 'ingot' | 'nugget' | 'gem' | 'dust' | 'ball' | 'stick' | 'coal' | 'bone'
  | 'string' | 'feather' | 'seed' | 'wheat' | 'bread' | 'apple' | 'meat'
  | 'fish' | 'egg' | 'book' | 'paper' | 'bucket' | 'bucket_full' | 'bow'
  | 'arrow' | 'pearl' | 'rod' | 'eye' | 'powder' | 'helmet' | 'chestplate'
  | 'leggings' | 'boots' | 'carrot' | 'potato' | 'melon_slice' | 'clay_ball'
  | 'brick' | 'saddle' | 'bed' | 'boat' | 'minecart' | 'star' | 'block_icon';

export interface ToolStats {
  kind: ToolKind;
  /** Harvest tier: 0 wood/gold, 1 stone, 2 iron, 3 diamond, 4 netherite. */
  tier: number;
  /** Mining speed multiplier against the matching material. */
  speed: number;
  /** Attack damage in half-hearts. */
  damage: number;
  /** Attack cooldown in ticks for the full-strength hit. */
  attackSpeed: number;
}

export interface ArmorStats {
  slot: 'head' | 'chest' | 'legs' | 'feet';
  /** Armour points (each is 4% damage reduction, capped). */
  defense: number;
  toughness: number;
}

export interface FoodStats {
  hunger: number;
  saturation: number;
  /** Ticks to eat (vanilla is 32 for most food). */
  eatTicks?: number;
}

export interface ItemDef {
  id: number;
  name: string;
  stack: number;
  sprite: ItemSpriteShape;
  colors: number[];
  block?: string;
  tool?: ToolStats;
  armor?: ArmorStats;
  food?: FoodStats;
  /** Max durability; 0 means the item is not damageable. */
  durability?: number;
  /** Ticks of furnace burn time this item provides as fuel. */
  fuel?: number;
  /** Item is consumed but the container is returned (bucket -> bucket). */
  containerItem?: string;
  /** Placed as a block when used on a face. */
  placesBlock?: string;
  /** Rarity affects the name colour in tooltips. */
  rare?: boolean;
}

const items: ItemDef[] = [];
const itemByName = new Map<string, ItemDef>();

function item(name: string, sprite: ItemSpriteShape, colors: number[], extra: Partial<ItemDef> = {}): ItemDef {
  if (itemByName.has(name)) throw new Error(`duplicate item ${name}`);
  const d: ItemDef = { id: items.length, name, stack: 64, sprite, colors, ...extra };
  items.push(d);
  itemByName.set(name, d);
  return d;
}

// ---------------------------------------------------------------------------
// Block items
// ---------------------------------------------------------------------------

/** Blocks that never appear as an inventory item. */
const NON_ITEM_BLOCKS = new Set([
  'air', 'cave_air', 'void_air', 'water', 'lava', 'fire', 'nether_portal',
  'end_portal', 'piston_head', 'moving_piston', 'wall_torch',
  'redstone_wall_torch', 'redstone_wire', 'wheat', 'carrots', 'potatoes',
  'nether_wart', 'tall_grass', 'farmland', 'end_portal_frame', 'spawner',
  'chorus_plant', 'melon', 'snow_layer',
]);

for (const b of BLOCKS) {
  if (NON_ITEM_BLOCKS.has(b.name)) continue;
  item(b.name, 'block_icon', [], { block: b.name, placesBlock: b.name });
}
// A few items place a different block than they are named after.
item('redstone', 'dust', [0xd42f2f], { placesBlock: 'redstone_wire' });
item('wheat_seeds', 'seed', [0x9a8a3a], { placesBlock: 'wheat' });
item('carrot', 'carrot', [0xf08020, 0x3a9a3a], { placesBlock: 'carrots', food: { hunger: 3, saturation: 3.6 } });
item('potato', 'potato', [0xd8c060], { placesBlock: 'potatoes', food: { hunger: 1, saturation: 0.6 } });
item('nether_wart_item', 'ball', [0xa82020], { placesBlock: 'nether_wart' });
item('snow_layer_item', 'ball', [0xf2fafa], { placesBlock: 'snow_layer' });

// ---------------------------------------------------------------------------
// Tool materials
// ---------------------------------------------------------------------------
interface Material {
  name: string;
  tier: number;
  speed: number;
  durability: number;
  damageBonus: number;
  color: number;
  accent: number;
}

const MATERIALS: Material[] = [
  { name: 'wooden', tier: 0, speed: 2, durability: 59, damageBonus: 0, color: 0xb08a51, accent: 0x8a6a3a },
  { name: 'stone', tier: 1, speed: 4, durability: 131, damageBonus: 1, color: 0x8a8a8a, accent: 0x6a6a6a },
  { name: 'iron', tier: 2, speed: 6, durability: 250, damageBonus: 2, color: 0xdcdcdc, accent: 0xa8a8a8 },
  { name: 'golden', tier: 0, speed: 12, durability: 32, damageBonus: 0, color: 0xf8d84a, accent: 0xc8a820 },
  { name: 'diamond', tier: 3, speed: 8, durability: 1561, damageBonus: 3, color: 0x5decd5, accent: 0x2ab0a0 },
  { name: 'netherite', tier: 4, speed: 9, durability: 2031, damageBonus: 4, color: 0x6a6068, accent: 0x44404a },
];

/** Base attack damage per tool class, before the material bonus. */
const TOOL_BASE: Record<string, { kind: ToolKind; damage: number; attackSpeed: number; sprite: ItemSpriteShape }> = {
  pickaxe: { kind: 'pickaxe', damage: 1, attackSpeed: 25, sprite: 'pickaxe' },
  axe: { kind: 'axe', damage: 5, attackSpeed: 40, sprite: 'axe' },
  shovel: { kind: 'shovel', damage: 1.5, attackSpeed: 20, sprite: 'shovel' },
  hoe: { kind: 'hoe', damage: 0, attackSpeed: 20, sprite: 'hoe' },
  sword: { kind: 'sword', damage: 3, attackSpeed: 12, sprite: 'sword' },
};

for (const m of MATERIALS) {
  for (const [toolName, base] of Object.entries(TOOL_BASE)) {
    item(`${m.name}_${toolName}`, base.sprite, [m.color, m.accent, 0x8a6a3a], {
      stack: 1,
      durability: toolName === 'sword' ? Math.round(m.durability * 1.0) : m.durability,
      tool: {
        kind: base.kind,
        tier: m.tier,
        speed: m.speed,
        damage: base.damage + m.damageBonus,
        attackSpeed: base.attackSpeed,
      },
      fuel: m.name === 'wooden' ? 200 : undefined,
    });
  }
}

// ---------------------------------------------------------------------------
// Armour
// ---------------------------------------------------------------------------
const ARMOR_MATERIALS: { name: string; color: number; accent: number; mult: number; defense: number[]; toughness: number }[] = [
  { name: 'leather', color: 0xa06540, accent: 0x7a4a2a, mult: 5, defense: [1, 3, 2, 1], toughness: 0 },
  { name: 'iron', color: 0xdcdcdc, accent: 0xa8a8a8, mult: 15, defense: [2, 6, 5, 2], toughness: 0 },
  { name: 'golden', color: 0xf8d84a, accent: 0xc8a820, mult: 7, defense: [2, 5, 3, 1], toughness: 0 },
  { name: 'diamond', color: 0x5decd5, accent: 0x2ab0a0, mult: 33, defense: [3, 8, 6, 3], toughness: 2 },
  { name: 'netherite', color: 0x6a6068, accent: 0x44404a, mult: 37, defense: [3, 8, 6, 3], toughness: 3 },
];
const ARMOR_SLOTS: { suffix: string; slot: ArmorStats['slot']; sprite: ItemSpriteShape; base: number }[] = [
  { suffix: 'helmet', slot: 'head', sprite: 'helmet', base: 11 },
  { suffix: 'chestplate', slot: 'chest', sprite: 'chestplate', base: 16 },
  { suffix: 'leggings', slot: 'legs', sprite: 'leggings', base: 15 },
  { suffix: 'boots', slot: 'feet', sprite: 'boots', base: 13 },
];
for (const m of ARMOR_MATERIALS) {
  ARMOR_SLOTS.forEach((s, i) => {
    item(`${m.name}_${s.suffix}`, s.sprite, [m.color, m.accent], {
      stack: 1,
      durability: s.base * m.mult,
      armor: { slot: s.slot, defense: m.defense[i]!, toughness: m.toughness },
    });
  });
}

// ---------------------------------------------------------------------------
// Materials, food and misc
// ---------------------------------------------------------------------------
item('stick', 'stick', [0x9a7440], { fuel: 100 });
item('coal', 'coal', [0x1a1a1a], { fuel: 1600 });
item('charcoal', 'coal', [0x2a2420], { fuel: 1600 });
item('iron_ingot', 'ingot', [0xdcdcdc, 0xa8a8a8]);
item('gold_ingot', 'ingot', [0xf8d84a, 0xc8a820]);
item('copper_ingot', 'ingot', [0xc06a3a, 0x9a5028]);
item('netherite_ingot', 'ingot', [0x6a6068, 0x44404a], { rare: true });
item('netherite_scrap', 'nugget', [0x7a5040, 0x5c4038]);
item('iron_nugget', 'nugget', [0xdcdcdc, 0xa8a8a8]);
item('gold_nugget', 'nugget', [0xf8d84a, 0xc8a820]);
item('diamond', 'gem', [0x5decd5, 0x2ab0a0]);
item('emerald', 'gem', [0x28d955, 0x18a03a]);
item('lapis_lazuli', 'dust', [0x2f52d4]);
item('quartz', 'gem', [0xece5dd, 0xc0b8b0]);
item('glowstone_dust', 'dust', [0xf7c96a]);
item('gunpowder', 'dust', [0x8a8a8a]);
item('blaze_powder', 'dust', [0xf0a020]);
item('blaze_rod', 'rod', [0xf8c840, 0xd89020], { fuel: 2400 });
item('ender_pearl', 'pearl', [0x149080, 0x0a5a50], { stack: 16 });
item('ender_eye', 'eye', [0x149080, 0xd8e060], { stack: 16 });
item('flint', 'gem', [0x3a3a3a, 0x1a1a1a]);
item('clay_ball', 'clay_ball', [0xa4a8b8]);
item('brick_item', 'brick', [0x985542]);
item('bone', 'bone', [0xe8e4d8]);
item('bone_meal', 'dust', [0xe8e4d8]);
item('string', 'string', [0xf0f0f0]);
item('feather', 'feather', [0xf8f8f8, 0xc8c8c8]);
item('leather', 'ingot', [0xa06540, 0x7a4a2a]);
item('paper', 'paper', [0xf0f0f0]);
item('book', 'book', [0x9a5a2a, 0xf0f0e0]);
item('slime_ball', 'ball', [0x7ac07a]);
item('magma_cream', 'ball', [0xf08020, 0x7ac07a]);
item('spider_eye', 'ball', [0x8a2a2a, 0xd8d8d8]);
item('rotten_flesh', 'meat', [0x8a5a3a, 0x6a3a2a], { food: { hunger: 4, saturation: 0.8 } });
item('bucket', 'bucket', [0xc8c8c8, 0x8a8a8a], { stack: 16 });
item('water_bucket', 'bucket_full', [0xc8c8c8, 0x3f76e4], { stack: 1, containerItem: 'bucket' });
item('lava_bucket', 'bucket_full', [0xc8c8c8, 0xd45a12], { stack: 1, containerItem: 'bucket', fuel: 20000 });
item('milk_bucket', 'bucket_full', [0xc8c8c8, 0xf8f8f8], { stack: 1, containerItem: 'bucket' });
item('shears', 'shears', [0xdcdcdc, 0x8a8a8a], { stack: 1, durability: 238, tool: { kind: 'shears', tier: 0, speed: 5, damage: 1, attackSpeed: 20 } });
item('flint_and_steel', 'flint_and_steel', [0xdcdcdc, 0x8a8a8a], { stack: 1, durability: 64 });
item('bow', 'bow', [0x9a7440, 0xf0f0f0], { stack: 1, durability: 384 });
item('arrow', 'arrow', [0x9a7440, 0xf0f0f0, 0x8a8a8a]);
item('wheat_item', 'wheat', [0xdcbb65]);
item('bread', 'bread', [0xc8934a], { food: { hunger: 5, saturation: 6 } });
item('apple', 'apple', [0xd02a2a, 0x3a8a2a], { food: { hunger: 4, saturation: 2.4 } });
item('golden_apple', 'apple', [0xf8d84a, 0x3a8a2a], { food: { hunger: 4, saturation: 9.6 }, rare: true });
item('porkchop', 'meat', [0xf0a0a0, 0xd06060], { food: { hunger: 3, saturation: 1.8 } });
item('cooked_porkchop', 'meat', [0xd08a4a, 0xa06030], { food: { hunger: 8, saturation: 12.8 } });
item('beef', 'meat', [0xd85a5a, 0xa03030], { food: { hunger: 3, saturation: 1.8 } });
item('cooked_beef', 'meat', [0x9a5a2a, 0x6a3a1a], { food: { hunger: 8, saturation: 12.8 } });
item('chicken', 'meat', [0xf0c0a0, 0xd09070], { food: { hunger: 2, saturation: 1.2 } });
item('cooked_chicken', 'meat', [0xd0a050, 0xa07030], { food: { hunger: 6, saturation: 7.2 } });
item('mutton', 'meat', [0xe08080, 0xb05050], { food: { hunger: 2, saturation: 1.2 } });
item('cooked_mutton', 'meat', [0xb07040, 0x805020], { food: { hunger: 6, saturation: 9.6 } });
item('cod', 'fish', [0xc8b090, 0x8a7060], { food: { hunger: 2, saturation: 0.4 } });
item('cooked_cod', 'fish', [0xd0a060, 0x907040], { food: { hunger: 5, saturation: 6 } });
item('baked_potato', 'potato', [0xd8a850], { food: { hunger: 5, saturation: 6 } });
item('melon_slice', 'melon_slice', [0xd02a2a, 0x3a8a2a], { food: { hunger: 2, saturation: 1.2 } });
item('pumpkin_pie', 'bread', [0xd8a850, 0xc07615], { food: { hunger: 8, saturation: 4.8 } });
item('sugar', 'dust', [0xf8f8f8]);
item('sugar_cane_item', 'stick', [0x94c56a], { placesBlock: 'sugar_cane' });
item('egg', 'egg', [0xf0e0c0, 0xc0a080], { stack: 16 });
item('saddle', 'saddle', [0x8a5a2a, 0x5a3a1a], { stack: 1 });
item('nether_star', 'star', [0xf8f8f8, 0xd8d8f8], { stack: 1, rare: true });
item('experience_bottle', 'ball', [0x60c020, 0xa0e060], { stack: 16 });
item('dragon_breath', 'ball', [0xc060f0, 0x6020a0], { stack: 16 });

const DYE_COLORS: [string, number][] = [
  ['white', 0xf0f0f0], ['orange', 0xf07613], ['magenta', 0xbd44b3], ['light_blue', 0x3aafd9],
  ['yellow', 0xf8c627], ['lime', 0x70b919], ['pink', 0xed8dac], ['gray', 0x3e4447],
  ['light_gray', 0x8e8e86], ['cyan', 0x158991], ['purple', 0x792aac], ['blue', 0x35399d],
  ['brown', 0x724728], ['green', 0x546d1b], ['red', 0xa12722], ['black', 0x141519],
];
for (const [name, color] of DYE_COLORS) item(`${name}_dye`, 'powder', [color]);

// ---------------------------------------------------------------------------
// Accessors
// ---------------------------------------------------------------------------

export const ITEMS: readonly ItemDef[] = items;
export const ITEM_COUNT = items.length;

export function itemDef(name: string): ItemDef {
  const d = itemByName.get(name);
  if (!d) throw new Error(`unknown item: ${name}`);
  return d;
}
export function itemById(id: number): ItemDef | undefined {
  return items[id];
}
export function itemId(name: string): number {
  return itemDef(name).id;
}
export function hasItem(name: string): boolean {
  return itemByName.has(name);
}

/** Item that a placed block should return to the inventory when broken. */
export function itemForBlock(block: string): ItemDef | undefined {
  return itemByName.get(block);
}

/** Block that an item places, if any. */
export function blockForItem(id: number): BlockDef | undefined {
  const d = items[id];
  if (!d?.placesBlock) return undefined;
  return blockDef(d.placesBlock);
}

export const ITEM_NAMES: readonly string[] = items.map((i) => i.name);

/** Items whose sprite is a rendered 3D block icon rather than a flat sprite. */
export const BLOCK_ICON_ITEMS = items.filter((i) => i.sprite === 'block_icon').map((i) => i.id);

/** Sprite-drawn (non-block) items, in the order they appear in the item atlas. */
export const SPRITE_ITEMS = items.filter((i) => i.sprite !== 'block_icon');
export const SPRITE_INDEX: ReadonlyMap<number, number> = new Map(
  SPRITE_ITEMS.map((it, i) => [it.id, i]),
);
